import React, { useRef, useEffect } from 'react';
import HrnbButton from '../controls/HrnbButton';
import { useAudioPlayer } from '../../context/AudioPlayerContext';
import { fetchWithAuth } from '../../utils/fetchWithAuth';
import { normalizeMediaUrl } from '../../utils/mediaUrl';
import '../../css/components/AudioPlayer.css';

const API_BASE = 'https://api.hrnb.ru';

/**
 * Глобальный аудиоплеер для всего портала
 * @param {Object} props
 * @param {boolean} props.standalone - Режим для страницы Audio (не фиксированный)
 * @param {boolean} props.forceVisible - Всегда показывать плеер (для страницы Audio)
 * @param {boolean} props.forceExpanded - Всегда разворачивать плеер (для страницы Audio)
 * @param {Array} props.tracks - Массив треков для standalone режима
 * @param {string} props.currentTrackId - ID текущего трека для standalone режима
 */
export default function AudioPlayer({
    standalone = false,
    forceVisible = false,
    forceExpanded = false,
    tracks: propTracks = [],
    currentTrackId: propCurrentTrackId = null,
    isPlaying: propIsPlaying = null,
    onIsPlayingChange = null
}) {
    const audioContext = useAudioPlayer();

    // Для forceExpanded/standalone режима используем локальное состояние или props
    const [localIsPlaying, setLocalIsPlaying] = React.useState(false);
    const [localCurrentTrackId, setLocalCurrentTrackId] = React.useState(propCurrentTrackId);

    // Используем props если переданы (для AudioPage), иначе локальное состояние или контекст
    const tracks = propTracks.length > 0 ? propTracks : audioContext.tracks;
    const currentTrackId = forceExpanded || standalone ? (propCurrentTrackId || localCurrentTrackId) : audioContext.currentTrackId;

    // Для forceExpanded используем props если переданы, иначе локальное состояние
    const isPlaying = forceExpanded && propIsPlaying !== null ? propIsPlaying : (standalone ? localIsPlaying : audioContext.isPlaying);

    const isCollapsed = forceExpanded ? false : (standalone ? false : audioContext.isCollapsed);
    const isVisible = forceVisible ? true : (standalone ? true : audioContext.isVisible);

    // Функции управления
    const playTrack = (trackId) => {
        if (forceExpanded || standalone) {
            setLocalCurrentTrackId(trackId);
            if (onIsPlayingChange) {
                onIsPlayingChange(true);
            } else {
                setLocalIsPlaying(true);
            }
        } else {
            audioContext.playTrack(trackId);
        }
    };

    const togglePlayPause = () => {
        if (forceExpanded || standalone) {
            const newIsPlaying = !isPlaying;
            if (onIsPlayingChange) {
                onIsPlayingChange(newIsPlaying);
            } else {
                setLocalIsPlaying(newIsPlaying);
            }
        } else {
            audioContext.togglePlayPause();
        }
    };

    const toggleCollapse = audioContext.toggleCollapse;
    const hidePlayer = audioContext.hidePlayer;

    // Преобразуем относительный URL в абсолютный
    const getFullUrl = React.useCallback((url) => {
        if (!url) return '';
        // blob: и data: URL — локальные, используются как есть
        if (url.startsWith('blob:') || url.startsWith('data:')) {
            return url;
        }
        // Нормализуем: старый путь /api/files/{id}/download переписывается
        // на /api/files/download/audio/{id} (общий эндпойнт отдаёт файл
        // только владельцу — 403 для остальных), голый ID — в аудио-эндпойнт
        const normalized = normalizeMediaUrl(url);
        if (/^https?:\/\//i.test(normalized)) {
            return normalized;
        }
        return `${API_BASE}${normalized}`;
    }, []);

    // Загружаем аудио через fetch с авторизацией и создаем Blob URL.
    // fetchWithAuth обновляет сессию при 401/истёкшем токене (как интерцепторы axios)
    const loadAudioWithAuth = React.useCallback(async (url) => {
        try {
            const fullUrl = getFullUrl(url);

            const response = await fetchWithAuth(fullUrl);

            if (!response.ok) {
                if (response.status === 401) {
                    console.error('[AudioPlayer] Unauthorized - session refresh failed');
                }
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            return blobUrl;
        } catch (error) {
            console.error('[AudioPlayer] Failed to load audio:', error);
            throw error;
        }
    }, [getFullUrl]);

    // Загружаем треки в контекст только если не forceExpanded (чтобы избежать циклов)
    React.useEffect(() => {
        if (propTracks.length > 0 && !forceExpanded) {
            audioContext.loadTracks(propTracks);
        }
    }, [propTracks.length, forceExpanded, audioContext]);

    if (!standalone && !forceVisible && !isVisible) {
        return null;
    }

    const currentTrack = tracks.find(t => t.id === currentTrackId);

    return (
        <div className={`audio-player ${standalone || forceExpanded ? '' : 'audio-player--fixed'} ${isCollapsed && !standalone && !forceExpanded ? 'audio-player--collapsed' : ''}`}>
            <AudioPlayerContent
                tracks={tracks}
                currentTrackId={currentTrackId}
                isPlaying={isPlaying}
                isCollapsed={isCollapsed}
                standalone={standalone}
                forceExpanded={forceExpanded}
                currentTrack={currentTrack}
                loadAudioWithAuth={loadAudioWithAuth}
                playTrack={playTrack}
                togglePlayPause={togglePlayPause}
                toggleCollapse={toggleCollapse}
                hidePlayer={hidePlayer}
            />
        </div>
    );
}

// Основной компонент с логикой аудио
function AudioPlayerContent({
    tracks,
    currentTrackId,
    isPlaying,
    isCollapsed,
    standalone,
    forceExpanded,
    currentTrack,
    loadAudioWithAuth,
    playTrack,
    togglePlayPause,
    toggleCollapse,
    hidePlayer
}) {
    const audioRef = React.useRef(null);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [volume, setVolume] = React.useState(0.7);
    const [isMuted, setIsMuted] = React.useState(false);
    const [currentBlobUrl, setCurrentBlobUrl] = React.useState(null);
    const [loadError, setLoadError] = React.useState(null);
    const [loadAttempt, setLoadAttempt] = React.useState(0);
    // Ключ последнего успешно загруженного трека ("id:mediaUrl")
    const loadedKeyRef = React.useRef(null);

    // Инициализация аудио
    React.useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = volume;
        }
    }, [volume]);

    // Обработка изменения трека - загрузка аудио.
    // Повторный выбор трека после ошибки запускает загрузку заново
    // (ключ успешной загрузки сбрасывается, loadAttempt меняется)
    React.useEffect(() => {
        const key = currentTrack ? `${currentTrackId}:${currentTrack.mediaUrl}` : null;

        // Этот же трек уже успешно загружен — не перезагружаем
        if (key && key === loadedKeyRef.current) {
            return;
        }
        loadedKeyRef.current = null;

        // Очищаем предыдущий Blob URL
        if (currentBlobUrl) {
            URL.revokeObjectURL(currentBlobUrl);
            setCurrentBlobUrl(null);
        }

        if (!currentTrack || !audioRef.current) {
            setLoadError(currentTrackId ? 'Трек не найден' : null);
            return;
        }

        let cancelled = false;

        const loadAudio = async () => {
            try {
                setLoadError(null);
                const blobUrl = await loadAudioWithAuth(currentTrack.mediaUrl);
                if (cancelled) {
                    URL.revokeObjectURL(blobUrl);
                    return;
                }
                loadedKeyRef.current = key;
                setCurrentBlobUrl(blobUrl);
                if (audioRef.current) {
                    audioRef.current.src = blobUrl;
                    audioRef.current.load();
                }
            } catch (err) {
                if (!cancelled) {
                    console.error('[AudioPlayer] Failed to load audio:', err);
                    setLoadError('Не удалось загрузить аудио');
                }
            }
        };

        loadAudio();

        return () => {
            cancelled = true;
        };
    }, [currentTrackId, currentTrack, loadAudioWithAuth, loadAttempt]);

    // Синхронизация play/pause - отдельный эффект
    React.useEffect(() => {
        if (audioRef.current && currentTrack && currentBlobUrl) {
            if (isPlaying) {
                audioRef.current.play().catch(err => {
                    console.error('[AudioPlayer] Playback error:', err);
                });
            } else {
                audioRef.current.pause();
            }
        }
    }, [isPlaying, currentTrack, currentTrackId, currentBlobUrl]);

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleEnded = () => {
        setCurrentTime(0);
        // Автопереключение на следующий трек
        if (currentTrackId) {
            const currentIndex = tracks.findIndex(t => t.id === currentTrackId);
            if (currentIndex < tracks.length - 1) {
                playTrack(tracks[currentIndex + 1].id);
            }
        }
    };

    // Ошибка воспроизведения (битый файл, неудачный decode)
    const handleAudioError = () => {
        setLoadError('Не удалось загрузить аудио');
    };

    // Повторная попытка загрузки
    const handleRetry = () => {
        loadedKeyRef.current = null;
        setLoadError(null);
        setLoadAttempt((a) => a + 1);
    };

    const handlePlayPause = () => {
        if (!currentTrack && tracks.length > 0) {
            playTrack(tracks[0].id);
        }
        togglePlayPause();
    };

    const handleSeek = (value) => {
        if (audioRef.current) {
            audioRef.current.currentTime = value;
            setCurrentTime(value);
        }
    };

    const handleVolumeChange = (value) => {
        setVolume(value);
        if (audioRef.current) {
            audioRef.current.volume = value;
        }
        setIsMuted(value === 0);
    };

    const handleToggleMute = () => {
        if (audioRef.current) {
            const newMuted = !isMuted;
            setIsMuted(newMuted);
            audioRef.current.muted = newMuted;
            if (newMuted) {
                setVolume(0);
            } else {
                setVolume(0.7);
                audioRef.current.volume = 0.7;
            }
        }
    };

    const handlePrevious = () => {
        if (currentTrackId) {
            const currentIndex = tracks.findIndex(t => t.id === currentTrackId);
            if (currentIndex > 0) {
                playTrack(tracks[currentIndex - 1].id);
            }
        }
    };

    const handleNext = () => {
        if (currentTrackId) {
            const currentIndex = tracks.findIndex(t => t.id === currentTrackId);
            if (currentIndex < tracks.length - 1) {
                playTrack(tracks[currentIndex + 1].id);
            }
        }
    };

    const formatTime = (seconds) => {
        if (!seconds || isNaN(seconds)) return '0:00';
        const mins = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    return (
        <>
            <audio
                ref={audioRef}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                onError={handleAudioError}
            />

            {loadError && (
                <div className="audio-player__error">
                    <small>{loadError}</small>
                    <button
                        type="button"
                        className="audio-player__retry-btn"
                        onClick={handleRetry}
                    >
                        Повторить
                    </button>
                </div>
            )}

            {standalone || forceExpanded || !isCollapsed ? (
                <>
                    {/* Основная панель: информация о треке + управление */}
                    <div className="audio-player__main-row">
                        {/* Информация о треке */}
                        {currentTrack && (
                            <div className="audio-player__track-section">
                                <div className="audio-player__track-info">
                                    <span className="audio-player__track-title">{currentTrack.title || 'Без названия'}</span>
                                    {(currentTrack.artist || currentTrack.authorName) && (
                                        <span className="audio-player__track-artist">
                                            {currentTrack.artist || currentTrack.authorName}
                                        </span>
                                    )}
                                    {currentTrack.battleTitle && (
                                        <span className="audio-player__track-battle">📀 {currentTrack.battleTitle}</span>
                                    )}
                                </div>
                                {isPlaying && (
                                    <div className="audio-player__equalizer">
                                        <div className="audio-player__equalizer-bar"></div>
                                        <div className="audio-player__equalizer-bar"></div>
                                        <div className="audio-player__equalizer-bar"></div>
                                        <div className="audio-player__equalizer-bar"></div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Кнопки управления */}
                        <div className="audio-player__controls">
                            <HrnbButton variant="icon" onClick={handlePrevious} disabled={!currentTrack}>
                                ⏮
                            </HrnbButton>
                            <HrnbButton
                                variant="primary"
                                size="lg"
                                onClick={handlePlayPause}
                                disabled={tracks.length === 0}
                            >
                                {isPlaying ? '⏸' : '▶'}
                            </HrnbButton>
                            <HrnbButton variant="icon" onClick={handleNext} disabled={!currentTrack}>
                                ⏭
                            </HrnbButton>
                        </div>

                        {/* Громкость */}
                        <div className="audio-player__volume-section">
                            <HrnbButton variant="icon" onClick={handleToggleMute}>
                                {isMuted ? '🔇' : '🔊'}
                            </HrnbButton>
                            <div className="audio-player__volume">
                                <input
                                    type="range"
                                    min="0"
                                    max="1"
                                    step="0.01"
                                    value={volume}
                                    onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
                                    className="audio-player__volume-slider"
                                />
                            </div>
                        </div>

                        {/* Кнопка сворачивания/закрытия - только для не-standalone и не-forceExpanded режима */}
                        {!standalone && !forceExpanded && (
                            <>
                                <HrnbButton variant="icon" size="sm" onClick={toggleCollapse}>
                                    {isCollapsed ? '▲' : '▼'}
                                </HrnbButton>
                                <HrnbButton variant="icon" size="sm" onClick={hidePlayer}>
                                    ✕
                                </HrnbButton>
                            </>
                        )}
                    </div>

                    {/* Прогресс-бар на всю ширину */}
                    <div className="audio-player__progress">
                        <input
                            type="range"
                            min="0"
                            max={duration || 100}
                            value={currentTime}
                            onChange={(e) => handleSeek(parseFloat(e.target.value))}
                            className="audio-player__slider"
                        />
                    </div>

                    {/* Плейлист */}
                    {tracks.length > 0 && (
                        <div className="audio-player__playlist">
                            <h4 className="audio-player__playlist-title">Плейлист ({tracks.length})</h4>
                            <div className="audio-player__playlist-items">
                                {tracks.map((track, index) => (
                                    <div
                                        key={track.id}
                                        className={`audio-player__playlist-item ${track.id === currentTrackId ? 'active' : ''}`}
                                        onClick={() => playTrack(track.id)}
                                    >
                                        <span className="audio-player__playlist-index">{index + 1}</span>
                                        <div className="audio-player__playlist-track-info">
                                            <span className="audio-player__playlist-track-title">{track.title || 'Без названия'}</span>
                                            {(track.artist || track.authorName) && (
                                                <span className="audio-player__playlist-track-artist">
                                                    {track.artist || track.authorName}
                                                </span>
                                            )}
                                            {track.battleTitle && (
                                                <span className="audio-player__playlist-track-battle">{track.battleTitle}</span>
                                            )}
                                        </div>
                                        <span className="audio-player__playlist-duration">{formatTime(track.duration)}</span>
                                        {track.id === currentTrackId && isPlaying && (
                                            <span className="audio-player__playlist-playing">🔊</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            ) : (
                <div className="audio-player__collapsed-content">
                    {currentTrack ? (
                        <div className="audio-player__track-info" onClick={toggleCollapse}>
                            <span className="audio-player__track-title">{currentTrack.title || 'Без названия'}</span>
                            <span className="audio-player__track-artist">
                                {currentTrack.artist || currentTrack.authorName || currentTrack.battleTitle || 'Неизвестный исполнитель'}
                            </span>
                        </div>
                    ) : (
                        <span className="audio-player__no-track">Нет трека</span>
                    )}
                    <div className="audio-player__controls">
                        <HrnbButton variant="icon" size="sm" onClick={handlePrevious} disabled={!currentTrack}>
                            ⏮
                        </HrnbButton>
                        <HrnbButton
                            variant="primary"
                            size="sm"
                            onClick={handlePlayPause}
                            disabled={tracks.length === 0}
                        >
                            {isPlaying ? '⏸' : '▶'}
                        </HrnbButton>
                        <HrnbButton variant="icon" size="sm" onClick={handleNext} disabled={!currentTrack}>
                            ⏭
                        </HrnbButton>
                        <HrnbButton variant="icon" size="sm" onClick={toggleCollapse}>
                            ▲
                        </HrnbButton>
                    </div>
                </div>
            )}
        </>
    );
}
